import { useEffect, useRef, useState } from 'react';
import { prepareInterview, type InterviewData, type InterviewStatus, type PrepareInterviewResponse } from '@/api/interviewApi';

export interface UsePrepareInterviewResult {
  isPreparing: boolean;
  isReady: boolean;
  error: Error | null;
  interviewStatus?: InterviewStatus;
  interviewData?: InterviewData;
  systemPromptStatus?: PrepareInterviewResponse['systemPromptStatus'];
  resolvedInitStatus?: InterviewStatus;
  preparedInSession: boolean;
  resumeFromTimestamp?: string;
}

const POLLING_INTERVAL = 3000; // 3 seconds

/**
 * Hook to prepare interview and poll for completion status
 * @param interview_id - The interview ID to prepare (optional)
 * @returns Object with isPreparing, isReady, and error states
 */
export function usePrepareInterview(interview_id?: string): UsePrepareInterviewResult {
  const [isPreparing, setIsPreparing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [interviewStatus, setInterviewStatus] = useState<InterviewStatus | undefined>(undefined);
  const [interviewData, setInterviewData] = useState<InterviewData | undefined>(undefined);
  const [systemPromptStatus, setSystemPromptStatus] = useState<PrepareInterviewResponse['systemPromptStatus'] | undefined>(undefined);
  const [resolvedInitStatus, setResolvedInitStatus] = useState<InterviewStatus | undefined>(undefined);
  const [preparedInSession, setPreparedInSession] = useState(false);
  const [resumeFromTimestamp, setResumeFromTimestamp] = useState<string | undefined>(undefined);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingInProgressRef = useRef(false);
  const isMountedRef = useRef(true);

  const updateFromResponse = (response: PrepareInterviewResponse) => {
    if (!isMountedRef.current) return;
    setSystemPromptStatus(response.systemPromptStatus);
    setInterviewData(response.interview);
    setInterviewStatus(response.interview?.interview_status);
    setResumeFromTimestamp(response.resumeFromTimestamp);
    const derivedStatus =
      response.systemPromptStatus === 'preparing' && response.interview?.interview_status === 'in_progress'
        ? 'not_started'
        : response.interview?.interview_status;
    setResolvedInitStatus(derivedStatus);
    setPreparedInSession(true);
  };

  useEffect(() => {
    // Reset state when interview_id changes
    setIsPreparing(false);
    setIsReady(false);
    setError(null);
    setInterviewStatus(undefined);
    setInterviewData(undefined);
    setSystemPromptStatus(undefined);
    setResolvedInitStatus(undefined);
    setPreparedInSession(false);
    setResumeFromTimestamp(undefined);
    isPollingInProgressRef.current = false;
    isMountedRef.current = true;

    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // If no interview_id, don't do anything
    if (!interview_id) {
      return;
    }

    // Start preparation immediately
    const startPreparation = async () => {
      // Prevent duplicate calls
      if (isPollingInProgressRef.current) return;

      try {
        isPollingInProgressRef.current = true;
        setIsPreparing(true);
        setError(null);
        const response = await prepareInterview(interview_id);
        updateFromResponse(response);

        if (!isMountedRef.current) {
          isPollingInProgressRef.current = false;
          return;
        }

        if (response.systemPromptStatus === 'completed') {
          setIsPreparing(false);
          setIsReady(true);
          isPollingInProgressRef.current = false;
          return;
        }

        // If status is 'preparing', start polling
        if (response.systemPromptStatus === 'preparing') {
          setIsPreparing(true);
          setIsReady(false);
          
          // Reset the flag so polling can start
          isPollingInProgressRef.current = false;

          // Start polling - ensure only one interval is active
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }

          pollingIntervalRef.current = setInterval(async () => {
            // Prevent concurrent poll calls - only one API call at a time
            if (isPollingInProgressRef.current) return;

            try {
              isPollingInProgressRef.current = true;
              const pollResponse = await prepareInterview(interview_id);
              updateFromResponse(pollResponse);

              if (!isMountedRef.current) {
                isPollingInProgressRef.current = false;
                return;
              }

              if (pollResponse.systemPromptStatus === 'completed') {
                setIsPreparing(false);
                setIsReady(true);
                isPollingInProgressRef.current = false;
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              } else if (pollResponse.systemPromptStatus === 'preparing') {
                // Continue polling
                setIsPreparing(true);
                setIsReady(false);
                isPollingInProgressRef.current = false;
              } else {
                isPollingInProgressRef.current = false;
              }
            } catch (pollError) {
              isPollingInProgressRef.current = false;
              if (!isMountedRef.current) return;
              setError(pollError instanceof Error ? pollError : new Error('Failed to poll preparation status'));
              setIsPreparing(false);
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }
          }, POLLING_INTERVAL);
        } else {
          isPollingInProgressRef.current = false;
        }
      } catch (err) {
        isPollingInProgressRef.current = false;
        if (!isMountedRef.current) return;
        setError(err instanceof Error ? err : new Error('Failed to prepare interview'));
        setIsPreparing(false);
        setIsReady(false);
      }
    };

    startPreparation();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      isPollingInProgressRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [interview_id]);

  return {
    isPreparing,
    isReady,
    error,
    interviewStatus,
    interviewData,
    systemPromptStatus,
    resolvedInitStatus,
    preparedInSession,
    resumeFromTimestamp,
  };
}
